import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { sdkTypes } from '@vansite/ts-sharetribe-flex-sdk';
import Decimal from 'decimal.js';
import React from 'react';
import { useIntl } from 'react-intl';

import config from '../../../config';
import { formatMoney } from '../../../util/currency';
import { FormattedMessage } from '../../../util/reactIntl';
import { txIsCanceled, txIsDeclined, txIsDelivered } from '../../../util/transaction.default';
import { itemValueStyles, lineItemTotalStyles, totalDividerStyles } from './BookingBreakdown.styles';

const { Money } = sdkTypes;

/**
 * Calculates the total price in sub units for multiple line items.
 */
const lineItemsTotal = (lineItems) => {
  const amount = lineItems.reduce((total, item) => {
    return total + item.lineTotal.amount;
  }, 0);
  const currency = lineItems[0] ? lineItems[0].lineTotal.currency : config.currency;
  return new Money(new Decimal(amount).round().toNumber(), currency);
};

/**
 * @param {Object} props
 * @param {Array} props.lineItems line items to total up, see adjustLineItemsForDisplay
 * @param {Object} props.transaction transaction the total is labelled after
 * @param {'customer'|'provider'} props.userRole role the breakdown is rendered for
 */
const LineItemTotalPrice = ({ lineItems, transaction, userRole }) => {
  const intl = useIntl();

  let providerTotalMessageId = 'BookingBreakdown.providerTotalDefault';
  if (txIsDelivered(transaction)) {
    providerTotalMessageId = 'BookingBreakdown.providerTotalDelivered';
  } else if (txIsDeclined(transaction)) {
    providerTotalMessageId = 'BookingBreakdown.providerTotalDeclined';
  } else if (txIsCanceled(transaction)) {
    providerTotalMessageId = 'BookingBreakdown.providerTotalDefault';
  }

  const totalLabel =
    userRole === 'provider' ? (
      <FormattedMessage id={providerTotalMessageId} />
    ) : (
      <FormattedMessage id="BookingBreakdown.total" />
    );

  const roleLineItems = lineItems.filter((item) => item.includeFor.includes(userRole));

  const total = lineItemsTotal(roleLineItems);
  const totalPrice = new Money(total.amount, total.currency);

  const formattedTotalPrice = formatMoney(intl, totalPrice);

  return (
    <>
      <Divider sx={totalDividerStyles} />
      <Box sx={lineItemTotalStyles}>
        <Typography fontWeight="regular" variant="lineItemLabel">{totalLabel}</Typography>
        <Typography sx={itemValueStyles} variant="lineItemHighlight">{formattedTotalPrice}</Typography>
      </Box>
    </>
  );
};

export default LineItemTotalPrice;
